import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./auth";
import { createRecipe, updateRecipe } from "./queries";
import { clean } from "./recipeClean";
import { slugify } from "./format";
import type { Recipe } from "./types";

// ── Types ──────────────────────────────────────────────────────────────

export type ChatMode = "create" | "revise";
export type ChatStatus = "closed" | "expanded" | "minimized";
export type StreamStatus = "idle" | "streaming" | "ready" | "saving" | "error";

export type ChatMessage = { role: "user" | "assistant"; content: string };

export type CapError = {
  kind: "weekly_new" | "weekly_revision" | "daily_tokens" | "monthly_tokens" | "other";
  message: string;
  resetAt?: string;
};

export type OpenOpts =
  | { mode: "create"; initialPrompt?: string }
  | { mode: "revise"; recipe: Recipe; initialPrompt?: string };

type ChatState = {
  status: ChatStatus;
  mode: ChatMode | null;
  stream: StreamStatus;
  messages: ChatMessage[];
  currentDraft: Recipe | Partial<Recipe> | null; // partial during streaming
  baseRecipe: Recipe | null;                     // revise mode only
  error: CapError | null;
  saveError: string | null;
  pendingPrompt: string | null;                  // auto-sent on next open
};

type ChatActions = {
  open: (opts: OpenOpts) => void;
  minimize: () => void;
  expand: () => void;
  close: () => void;
  abort: () => void;
  sendMessage: (prompt: string) => Promise<void>;
  save: () => Promise<void>;
  discardDraft: () => void;
};

type ChatContextValue = ChatState & ChatActions;

const ChatContext = createContext<ChatContextValue | null>(null);

// ── Partial-JSON parser ───────────────────────────────────────────────
// The chat proxy streams Anthropic's `input_json_delta` chunks. We accumulate
// them and try to parse on every update so the UI can render fields as they
// arrive (title first, then macros, then ingredient groups, etc.).
function parsePartialJson(text: string): unknown | undefined {
  if (!text) return undefined;
  try { return JSON.parse(text); } catch { /* fall through */ }

  let inString = false;
  let escape = false;
  const stack: string[] = [];
  for (const ch of text) {
    if (escape) { escape = false; continue; }
    if (inString) {
      if (ch === "\\") escape = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') { inString = true; continue; }
    if (ch === "{") stack.push("}");
    else if (ch === "[") stack.push("]");
    else if (ch === "}" || ch === "]") stack.pop();
  }

  let fixed = text.replace(/,\s*$/, "");
  // Strip a trailing key-without-value like  ..."servings":
  fixed = fixed.replace(/,?\s*"[^"]*"\s*:\s*$/, "");
  if (inString) fixed += '"';
  while (stack.length) fixed += stack.pop();

  try { return JSON.parse(fixed); } catch { return undefined; }
}

// ── Provider ──────────────────────────────────────────────────────────

const CHAT_URL = import.meta.env.VITE_CHAT_URL ?? "http://localhost:8081";

const INITIAL: ChatState = {
  status: "closed",
  mode: null,
  stream: "idle",
  messages: [],
  currentDraft: null,
  baseRecipe: null,
  error: null,
  saveError: null,
  pendingPrompt: null,
};

export function ChatProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState<ChatState>(INITIAL);
  const abortRef = useRef<AbortController | null>(null);

  // Abort any in-flight stream when the app goes to background (iOS Capacitor)
  // or the user signs out. Either case: tokens are getting wasted.
  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState === "hidden") {
        abortRef.current?.abort();
      }
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (!user) {
      abortRef.current?.abort();
      setState(INITIAL);
    }
  }, [user]);

  const open = useCallback((opts: OpenOpts) => {
    if (opts.mode === "create") {
      setState({
        ...INITIAL,
        status: "expanded",
        mode: "create",
        pendingPrompt: opts.initialPrompt ?? null,
      });
    } else {
      setState({
        ...INITIAL,
        status: "expanded",
        mode: "revise",
        baseRecipe: opts.recipe,
        currentDraft: opts.recipe,
        pendingPrompt: opts.initialPrompt ?? null,
      });
    }
  }, []);

  const minimize = useCallback(() => {
    setState((s) => (s.status === "closed" ? s : { ...s, status: "minimized" }));
  }, []);

  const expand = useCallback(() => {
    setState((s) => (s.status === "closed" ? s : { ...s, status: "expanded" }));
  }, []);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    setState((s) => (s.stream === "streaming" ? { ...s, stream: "idle" } : s));
  }, []);

  const close = useCallback(() => {
    abortRef.current?.abort();
    setState(INITIAL);
  }, []);

  const discardDraft = useCallback(() => {
    setState((s) =>
      s.mode === "revise" && s.baseRecipe
        ? { ...s, currentDraft: s.baseRecipe, messages: [], stream: "idle", error: null, saveError: null }
        : { ...s, currentDraft: null, messages: [], stream: "idle", error: null, saveError: null }
    );
  }, []);

  const sendMessage = useCallback(
    async (prompt: string): Promise<void> => {
      if (!user) return;
      const trimmed = prompt.trim();
      if (!trimmed) return;

      const idToken = await user.getIdToken();

      // Build the request body and updated messages list. For revise mode,
      // include all prior turns; for create mode, a single prompt.
      const userMsg: ChatMessage = { role: "user", content: trimmed };
      const mode = state.mode ?? "create";

      let body: unknown;
      if (mode === "create") {
        body = { mode: "create", prompt: trimmed };
      } else {
        if (!state.baseRecipe) {
          setState((s) => ({ ...s, stream: "error", saveError: "Revise mode requires a base recipe." }));
          return;
        }
        body = {
          mode: "revise",
          recipeId: state.baseRecipe.id,
          currentDraft: state.currentDraft ?? state.baseRecipe,
          messages: [...state.messages, userMsg],
        };
      }

      setState((s) => ({
        ...s,
        stream: "streaming",
        messages: [...s.messages, userMsg],
        error: null,
        saveError: null,
        // In create mode, clear any prior draft. In revise mode, keep the
        // current draft visible until the new one streams in.
        currentDraft: mode === "create" ? null : s.currentDraft,
      }));

      const controller = new AbortController();
      abortRef.current?.abort();
      abortRef.current = controller;

      let resp: Response;
      try {
        resp = await fetch(`${CHAT_URL}/chat/generate`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });
      } catch (e: unknown) {
        if ((e as { name?: string })?.name === "AbortError") {
          setState((s) => ({ ...s, stream: "idle" }));
          return;
        }
        setState((s) => ({
          ...s,
          stream: "error",
          error: { kind: "other", message: "Network error reaching chat service." },
        }));
        return;
      }

      // 429: cap exceeded. JSON body, not SSE.
      if (resp.status === 429) {
        const payload = await resp.json().catch(() => ({}));
        setState((s) => ({
          ...s,
          stream: "error",
          error: {
            kind: payload.kind ?? "other",
            message: payload.error ?? "You've hit your usage cap.",
            resetAt: payload.resetAt,
          },
        }));
        return;
      }
      if (!resp.ok || !resp.body) {
        const text = await resp.text().catch(() => "");
        setState((s) => ({
          ...s,
          stream: "error",
          error: { kind: "other", message: text || `Chat service error (${resp.status}).` },
        }));
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let accJson = "";

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE events are separated by blank lines.
          const parts = buffer.split("\n\n");
          buffer = parts.pop() ?? "";
          for (const part of parts) {
            const line = part.split("\n").find((l) => l.startsWith("data:"));
            if (!line) continue;
            const payload = line.slice(5).trim();
            if (!payload) continue;
            let event: { type: string; [k: string]: unknown };
            try {
              event = JSON.parse(payload);
            } catch {
              continue;
            }

            if (event.type === "delta") {
              accJson += (event as unknown as { text: string }).text;
              const parsed = parsePartialJson(accJson);
              if (parsed && typeof parsed === "object") {
                setState((s) => ({ ...s, currentDraft: parsed as Partial<Recipe> }));
              }
            } else if (event.type === "done") {
              const recipe = (event as unknown as { recipe: Recipe }).recipe;
              setState((s) => ({
                ...s,
                stream: "ready",
                currentDraft: recipe,
                messages: [
                  ...s.messages,
                  { role: "assistant", content: assistantSummary(recipe, s.mode!) },
                ],
              }));
            } else if (event.type === "message") {
              // Revise mode only: model answered conversationally instead of
              // revising. Append the text as an assistant message; the draft
              // is unchanged.
              const text = String((event as { text?: string }).text ?? "");
              setState((s) => ({
                ...s,
                stream: "idle",
                messages: [...s.messages, { role: "assistant", content: text }],
              }));
            } else if (event.type === "error") {
              setState((s) => ({
                ...s,
                stream: "error",
                error: { kind: "other", message: String(event.message ?? "Generation failed.") },
              }));
            }
          }
        }
      } catch (e: unknown) {
        if ((e as { name?: string })?.name !== "AbortError") {
          setState((s) => ({
            ...s,
            stream: "error",
            error: { kind: "other", message: "Stream interrupted." },
          }));
        } else {
          setState((s) => ({ ...s, stream: "idle" }));
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [user, state.mode, state.baseRecipe, state.currentDraft, state.messages]
  );

  const save = useCallback(async (): Promise<void> => {
    if (!user || !state.currentDraft || !state.mode) return;
    setState((s) => ({ ...s, stream: "saving", saveError: null }));

    // Run the same cleanup the manual NewRecipe form does so blank/empty
    // values don't pollute Firestore.
    const draft = state.currentDraft as Recipe;
    const cleaned = (clean(draft) ?? {}) as Recipe;
    cleaned.id = (draft.id ?? "").trim() || slugify(draft.title ?? "untitled");
    cleaned.title = draft.title?.trim() ?? "Untitled";

    try {
      if (state.mode === "create") {
        await createRecipe(user.uid, cleaned);
      } else {
        if (!state.baseRecipe) throw new Error("Missing base recipe for revise.");
        // Defensive: force id to the original even if the model wandered.
        cleaned.id = state.baseRecipe.id;
        await updateRecipe(user.uid, state.baseRecipe.id, cleaned, "Revised via chat");
      }
      const targetId = cleaned.id;
      setState(INITIAL);
      navigate(`/recipe/${targetId}`);
    } catch (e: unknown) {
      const message = (e as { message?: string })?.message ?? "Could not save this recipe.";
      setState((s) => ({ ...s, stream: "error", saveError: message }));
    }
  }, [user, state.currentDraft, state.mode, state.baseRecipe, navigate]);

  // Auto-fire any initialPrompt queued by `open()` once the chat is in a sendable state.
  useEffect(() => {
    if (!state.pendingPrompt) return;
    if (state.status !== "expanded" || state.stream !== "idle") return;
    const prompt = state.pendingPrompt;
    setState((s) => ({ ...s, pendingPrompt: null }));
    sendMessage(prompt);
  }, [state.pendingPrompt, state.status, state.stream, sendMessage]);

  const value = useMemo<ChatContextValue>(
    () => ({
      ...state,
      open,
      minimize,
      expand,
      close,
      abort,
      sendMessage,
      save,
      discardDraft,
    }),
    [state, open, minimize, expand, close, abort, sendMessage, save, discardDraft]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used within ChatProvider");
  return ctx;
}

// Short summary appended to the assistant message log after a successful
// generation. The full recipe lives in currentDraft / the preview pane.
function assistantSummary(recipe: Recipe, mode: ChatMode): string {
  const title = recipe.title ?? "your recipe";
  if (mode === "create") return `Generated "${title}".`;
  const mods = recipe.aiProvenance?.modifications ?? [];
  if (mods.length === 0) return "Updated.";
  return `Updated. ${mods[mods.length - 1]}`;
}
